import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAdminContext } from '../../context/AdminContext.js';
import Pagination from '../../components/common/Pagination.js';
import '../../styles/Reportes.css'; // Reutilizamos estilos de reportes
import '../../styles/RegistroFinanciero.css';
import { Search, Calendar } from 'lucide-react';

const LOG_TYPE_LABELS = {
    todos: 'Todos los Tipos',
    ganancia_roi: 'Participación asignada',
    commission: 'Comisión',
    retiro_aprobado: 'Retiro Aprobado',
    reembolso_retiro: 'Reembolso Retiro',
    fee_acelerador: 'Fee Acelerador',
    fee_acelerador_manual: 'Fee Acelerador Manual',
    fee_reactivacion_acelerador: 'Fee Reactivación Acelerador',
    fee_retiro: 'Fee Retiro',
    inversion_manual: 'Participación Manual',
    inversion_manual_test: 'Participación Manual (Prueba)',
    cancelacion_acuerdo: 'Cancelación Acuerdo',
    ajuste_manual: 'Ajuste Manual',
    reversion_ganancia: 'Reversión de Resultado'
};

const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '$0.00';
    return amount.toLocaleString('es-ES', { style: 'currency', currency: 'USD' });
};

const RegistroFinanciero = () => {
    const {
        users, // Obtenemos la lista de usuarios para los nombres
        getFinancialLog, // <-- NUEVO: Función para obtener datos paginados
    } = useAdminContext();

    // --- ESTADOS LOCALES PARA DATOS Y PAGINACIÓN ---
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paginationInfo, setPaginationInfo] = useState({
        totalCount: 0,
        lastVisibleId: null,
        hasMore: true
    });
    const pageCursorsRef = useRef({ 1: null });

    // --- ESTADOS PARA FILTROS ---
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('todos');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    // --- NUEVO: Estados para filtros de fecha ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // --- PAGINATION STATE ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    
    // Crear un mapa de usuarios para búsqueda rápida de nombres
    const userMap = useMemo(() => {
        if (!users) return new Map();
        return new Map(users.map(user => [user.id, { fullName: user.fullName, userEmail: user.userEmail }]));
    }, [users]);

    // Debounce search term
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timerId);
    }, [searchTerm]);

    // --- NUEVO: Lógica de carga de datos desde el servidor ---
    const fetchFinancialData = useCallback(async (page, filters) => {
        setLoading(true);
        setError(null);

        try {
            // Asegurar que las fechas vacías se envíen como null
            const requestFilters = {
                ...filters,
                startDate: filters.startDate || null,
                endDate: filters.endDate || null
            };

            const response = await getFinancialLog({
                filters: requestFilters,
                pagination: {
                    itemsPerPage,
                    lastVisibleId: pageCursorsRef.current[page - 1] || null,
                },
            });

            if (response.data.success) {
                const { logs: newLogs, totalCount, lastVisibleId, hasMore } = response.data;

                // Mapear los logs para añadirles el nombre del usuario
                const logsWithUserData = newLogs.map(log => {
                    const userData = userMap.get(log.userId);
                    return {
                        ...log,
                        userName: userData?.fullName || 'N/A',
                        userEmail: userData?.userEmail || log.userEmail || 'N/A'
                    };
                });

                setLogs(logsWithUserData);
                
                // Actualizar referencia de cursores sin disparar re-render
                pageCursorsRef.current[page] = lastVisibleId;

                setPaginationInfo(prev => ({
                    ...prev,
                    totalCount,
                    hasMore
                }));
            } else {
                throw new Error('La respuesta del servidor no fue exitosa.');
            }
        } catch (err) {
            console.error("Error fetching financial log:", err);
            setError("No se pudo cargar el registro financiero.");
        } finally {
            setLoading(false);
        }
    }, [getFinancialLog, itemsPerPage, userMap]);

    // Efecto unificado para manejar cambios de filtros y paginación sin condiciones de carrera
    const prevFiltersRef = useRef({ searchTerm: '', filterType: 'todos', startDate: '', endDate: '', itemsPerPage: 20 });

    useEffect(() => {
        const prev = prevFiltersRef.current;
        const filtersChanged = 
            prev.searchTerm !== debouncedSearchTerm ||
            prev.filterType !== filterType ||
            prev.startDate !== startDate ||
            prev.endDate !== endDate ||
            prev.itemsPerPage !== itemsPerPage;

        if (filtersChanged) {
            prevFiltersRef.current = { searchTerm: debouncedSearchTerm, filterType, startDate, endDate, itemsPerPage };
            pageCursorsRef.current = { 1: null };
            if (currentPage !== 1) {
                setCurrentPage(1);
                return;
            }
        }

        fetchFinancialData(currentPage, { searchTerm: debouncedSearchTerm, filterType, startDate, endDate });
    }, [currentPage, debouncedSearchTerm, filterType, itemsPerPage, startDate, endDate, fetchFinancialData]);

    const totalPages = Math.ceil(paginationInfo.totalCount / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };
    const logTypes = useMemo(() => [
        'todos', 'ganancia_roi', 'commission', 'retiro_aprobado', 'reembolso_retiro', 'fee_acelerador', 
        'fee_acelerador_manual', 'fee_reactivacion_acelerador', 'fee_retiro', 'inversion_manual', 
        'inversion_manual_test', 'cancelacion_acuerdo', 'ajuste_manual', 'reversion_ganancia'
    ].sort(), []);

    return (
        <div className="main-panel">
            <h2 className="section-title">Registro Financiero Global</h2>
            <p>Historial completo de todas las transacciones financieras de la plataforma.</p>

            <div className="report-filters" style={{ marginTop: '1.5rem' }}>
                <div className="filter-item">
                    <Search size={16} />
                    <input 
                        type="text"
                        placeholder="Buscar por email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none' }} disabled={debouncedSearchTerm.length > 0}>
                        {logTypes.map(type => (
                            <option key={type} value={type}>{LOG_TYPE_LABELS[type] || type.replace(/_/g, ' ')}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-item">
                    <Calendar size={16} />
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none' }} disabled={debouncedSearchTerm.length > 0} />
                </div>
                 <div className="filter-item">
                    <Calendar size={16} />
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none' }} disabled={debouncedSearchTerm.length > 0} />
                </div>
                {(startDate || endDate) && (
                    <button 
                        onClick={() => { setStartDate(''); setEndDate(''); }}
                        style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', marginLeft: '8px', display: 'flex', alignItems: 'center' }}
                        title="Limpiar fechas"
                    >
                        <span style={{ fontSize: '18px', fontWeight: 'bold' }}>×</span>
                    </button>
                )}
            </div>
            {debouncedSearchTerm.length > 0 && (
                <p className="search-priority-notice">La búsqueda por email tiene prioridad sobre el filtro de tipo.</p>
            )}

            <div className="report-table-container">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th style={{ width: '20%' }}>Usuario</th>
                            <th>Tipo</th>
                            <th>Monto</th>
                            <th>Descripción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="empty-state-text">Cargando...</td></tr>
                        ) : error ? (
                            <tr><td colSpan="5" className="empty-state-text" style={{ color: '#dc3545' }}>{error}</td></tr>
                        ) : logs.length > 0 ? logs.map(log => (
                            <tr key={log.id}>
                                    <td data-label="Fecha">
                                        {(() => {
                                            if (!log.createdAt) return 'N/A';
                                            if (log.createdAt.toDate) return log.createdAt.toDate().toLocaleString('es-ES'); // Firestore Timestamp (cliente)
                                            if (log.createdAt._seconds) return new Date(log.createdAt._seconds * 1000).toLocaleString('es-ES'); // Timestamp serializado (servidor)
                                            const d = new Date(log.createdAt);
                                            return !isNaN(d.getTime()) ? d.toLocaleString('es-ES') : 'Fecha inválida';
                                        })()}
                                    </td>
                                    <td data-label="Usuario">
                                        <div className="user-cell">
                                            <span>{log.userName}</span>
                                            <small>{log.userEmail}</small>
                                        </div>
                                    </td>
                                    <td data-label="Tipo"><span className={`log-type-badge type-${log.type || 'default'}`}>{log.type ? log.type.replace(/_/g, ' ') : 'Desconocido'}</span></td>
                                    <td data-label="Monto" className={log.amount >= 0 ? 'amount-positive' : 'amount-negative'}>
                                        {log.amount >= 0 ? '+' : ''}{formatCurrency(log.amount)}
                                    </td>
                                    <td data-label="Descripción" title={log.description || ''}>{log.description}</td>
                                </tr>
                        )) : (
                            <tr><td colSpan="5" className="empty-state-text" style={{ padding: '2rem' }}>No hay registros que coincidan con los filtros.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {paginationInfo.totalCount > 0 && (
                <Pagination
                    totalItems={paginationInfo.totalCount}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={setItemsPerPage}
                    currentPage={currentPage}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    canNext={currentPage < totalPages}
                    canPrevious={currentPage > 1}
                />
            )}
        </div>    );
};

export default RegistroFinanciero;